<script setup lang="ts">
import type { CompetitionSnapshot } from './competition-types';
import type { Music } from '~/services/musicService';
import type { ListSong } from './rankings';
import { rankingPage } from './rankings';
import { entryProgress } from './competition.mjs';
import { useDailyChallenge } from './useDailyChallenge';
import { useAuthState } from '~/composables/useAuthState';
import DailyChallengeCard from './DailyChallengeCard.vue';
import CollectionSongs from './CollectionSongs.vue';
import CoverArt from './CoverArt.vue';
import FeedShare from './FeedShare.vue';
import MmButton from '../base/MmButton.vue';
import MmDisclosure from '../base/MmDisclosure.vue';
import MmIcon from '../base/MmIcon.vue';
import MmProgress from '../base/MmProgress.vue';
import MmSkeleton from '../base/MmSkeleton.vue';
const props = defineProps<{ chart?: CompetitionSnapshot }>();
const { t, locale } = useI18n(),
  localePath = useLocalePath();
const {
  data: mine,
  loading,
  isAuthReady,
  isAuthenticated,
} = useDailyChallenge();
const { handleSignIn, isLoggingIn } = useAuthState();
const share = ref<Music | null>(null);
watch([isAuthenticated, locale], () => {
  share.value = null;
});
const {
  data: songs,
  pending,
  error,
  refresh,
} = await useAsyncData(
  () => `daily-challenge-songs-${locale.value}`,
  async () => {
    const fresh = (await rankingPage('music_creator_new', 1, 10, locale.value))
      .data.list as ListSong[];
    const trending =
      fresh.length < 4
        ? ((await rankingPage('music_global_trending', 1, 6, locale.value)).data
            .list as ListSong[])
        : [];
    return { fresh: fresh.slice(0, 10), trending: trending.slice(0, 6) };
  },
);
const number = (value: number) =>
  new Intl.NumberFormat(locale.value).format(value);
const awardDate = computed(
  () => props.chart?.last_awarded?.settlement_day || '',
);
</script>
<template>
  <div class="chart-wrap daily-challenge" data-daily-challenge>
    <DailyChallengeCard class="challenge-hero" hero :chart="chart" />
    <div class="challenge-content">
      <section
        class="challenge-mine"
        :aria-label="t('mm.challenge.mine')"
        :aria-busy="loading || !isAuthReady"
      >
        <h2>{{ t('mm.challenge.mine') }}</h2>
        <div
          v-if="loading || !isAuthReady"
          class="challenge-entry-skeleton"
          aria-hidden="true"
        >
          <MmSkeleton width="52px" height="52px" />
          <div>
            <MmSkeleton width="65%" height="20px" /><MmSkeleton
              height="14px"
            /><MmSkeleton height="6px" />
          </div>
        </div>
        <div
          v-else-if="isAuthenticated && mine?.entries.length"
          class="challenge-entries"
        >
          <article
            v-for="entry in mine.entries"
            :key="entry.song_id"
            class="challenge-entry"
            data-challenge-entry
          >
            <CoverArt :src="entry.cover_url" alt="" :width="52" :height="52" />
            <div class="challenge-entry-copy">
              <NuxtLink
                :to="
                  localePath(`/music/song/${encodeURIComponent(entry.song_id)}`)
                "
                >{{ entry.title }}</NuxtLink
              >
              <p>
                {{
                  entry.listeners_today >= mine.min_song_listeners
                    ? t('mm.challenge.qualified')
                    : t('mm.challenge.listeners', {
                        count: number(entry.listeners_today),
                        target: number(mine.min_song_listeners),
                      })
                }}
              </p>
              <MmProgress
                :value="
                  entryProgress(entry.listeners_today, mine.min_song_listeners)
                "
                :label="
                  t('mm.challenge.listeners', {
                    count: number(entry.listeners_today),
                    target: number(mine.min_song_listeners),
                  })
                "
              />
            </div>
            <MmButton
              variant="secondary"
              :aria-label="`${t('mm.feed.share')} · ${entry.title}`"
              @click="
                share = {
                  id: entry.song_id,
                  title: entry.title,
                  image_url: entry.cover_url,
                } as Music
              "
              ><MmIcon name="share" :size="18" />{{
                t('mm.feed.share')
              }}</MmButton
            >
          </article>
        </div>
        <div v-else class="challenge-publish" data-challenge-publish>
          <MmIcon name="disc" :size="32" />
          <p>{{ t('mm.challenge.publish') }}</p>
          <NuxtLink
            v-if="isAuthenticated"
            :to="localePath('/create')"
            class="btn btn-primary"
            >{{ t('incInsights.create') }}</NuxtLink
          ><MmButton v-else :loading="isLoggingIn" @click="handleSignIn">{{
            t('mm.challenge.signIn')
          }}</MmButton>
        </div>
      </section>
      <section data-challenge-fresh>
        <h2>{{ t('mm.challenge.fresh') }}</h2>
        <CollectionSongs
          variant="collection"
          source="chart"
          :is-loading-more="false"
          :has-more="false"
          :auto-load-more="false"
          ranking-key="music_creator_new"
          hide-ranks
          :music-list="songs?.fresh || []"
          :is-loading="pending"
          :error="error ? t('common.load_failed') : ''"
          @reload="refresh"
        />
      </section>
      <section v-if="songs && songs.fresh.length < 4" data-challenge-trending>
        <h2>{{ t('mm.challenge.trending') }}</h2>
        <CollectionSongs
          variant="collection"
          source="chart"
          :is-loading-more="false"
          :has-more="false"
          :auto-load-more="false"
          ranking-key="music_global_trending"
          :is-loading="false"
          :error="null"
          :music-list="songs.trending"
        />
      </section>
      <section v-if="chart?.last_awarded" data-last-awarded>
        <h2>{{ t('mm.challenge.last', { date: awardDate }) }}</h2>
        <div
          v-for="song in chart.last_awarded.items.slice(0, 10)"
          :key="song.id"
          class="challenge-awarded"
        >
          <span
            v-if="song.award_rank === 1"
            class="challenge-trophy"
            role="img"
            :aria-label="t('mm.challenge.top1')"
            ><MmIcon name="trophy" :size="22" /></span
          ><span v-else class="challenge-award-rank">{{ song.award_rank }}</span
          ><CollectionSongs
            variant="collection"
            source="chart"
            :is-loading-more="false"
            :has-more="false"
            :auto-load-more="false"
            hide-ranks
            :is-loading="false"
            :error="null"
            ranking-key="music_daily_competition"
            :music-list="[song]"
          />
        </div>
      </section>
      <MmDisclosure
        class="challenge-rules"
        variant="divider"
        :label="t('mm.challenge.rules')"
        ><p>{{ t('mm.challenge.settlement') }}</p>
        <p>{{ t('mm.challenge.order') }}</p>
        <p>{{ t('mm.challenge.scaleRule') }}</p>
        <NuxtLink :to="localePath('/rewards?section=badge&view=history')"
          >{{ t('incInsights.historyLink') }} →</NuxtLink
        ></MmDisclosure
      >
    </div>
    <FeedShare :song="share" @close="share = null" />
  </div>
</template>
<style scoped>
.daily-challenge {
  grid-column: 1 / -1;
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: start;
  gap: var(--s-6);
  min-width: 0;
}
.challenge-content {
  display: grid;
  gap: var(--s-8);
  min-width: 0;
}
@media (min-width: 1024px) {
  .daily-challenge {
    grid-template-columns: 340px minmax(0, 1fr);
    gap: 36px;
  }
  .challenge-hero {
    position: sticky;
    top: calc(var(--nav-h) + 16px);
  }
}
h2 {
  font: 600 var(--t-h3) / 1.4 var(--font-ui);
  margin-bottom: var(--s-4);
}
.challenge-mine {
  padding: var(--s-5);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  background: var(--surface);
}
.challenge-publish {
  display: grid;
  justify-items: center;
  gap: var(--s-4);
  padding-block: var(--s-5);
  text-align: center;
}
.challenge-entries {
  display: grid;
  gap: var(--s-5);
}
.challenge-entry,
.challenge-entry-skeleton {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--s-4);
  min-height: 88px;
}
.challenge-entry-copy,
.challenge-entry-skeleton > div {
  display: grid;
  gap: var(--s-2);
  min-width: 0;
}
.challenge-entry-copy a {
  font-weight: 600;
  overflow-wrap: anywhere;
}
.challenge-entry-copy p {
  font-size: var(--t-small);
  color: var(--text-2);
}
.challenge-entry :deep(.mm-progress) {
  height: 6px;
}
.challenge-entry :deep(.mm-progress span) {
  background: var(--text-3);
}
.challenge-awarded {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  align-items: center;
  gap: var(--s-2);
}
.challenge-trophy {
  color: var(--warning-text);
}
.challenge-award-rank {
  font-family: var(--font-mono);
  text-align: center;
}
.challenge-rules {
  border: 0;
  border-block: 1px solid var(--line);
  border-radius: 0;
  background: transparent;
}
.challenge-rules :deep(> summary) {
  min-height: 52px;
  padding: 0;
}
.challenge-rules :deep(.disclosure-title) {
  font-weight: 400;
}
.challenge-rules :deep(> .body) {
  display: grid;
  gap: var(--s-3);
  padding: 0 0 var(--s-5);
  color: var(--text-2);
  font-size: var(--t-small);
  line-height: 1.7;
}
a:focus-visible,
summary:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 3px;
}
@media (max-width: 479px) {
  .challenge-entry {
    grid-template-columns: 52px minmax(0, 1fr);
    gap: var(--s-3);
  }
  .challenge-entry > .btn {
    grid-column: 2;
    justify-self: start;
  }
}
</style>
